import ResponseError from 'services/graphql/response-error';
import Response from 'services/graphql/response';
import { useTranslation } from 'i18n';
import { toast } from 'services/toast';
import { copySurfaceNodesMutation } from 'graphql/surface-node/mutations/copy-surface-nodes';
import useMutation from 'hooks/use-mutation';
import useSaveNodesInCache from 'components/canvas/hooks/use-save-nodes-in-cache';
import useFindNodeInCache from 'components/canvas/hooks/use-find-node';
import { Position } from 'types';

interface CopySurfaceNodesInput {
  id: string;
  surface?: string;
  position?: Position;
}

export default function useCopySurfaceNodes(
  surfaceId: string,
): (
  input: CopySurfaceNodesInput[],
) => Promise<any | Response<any> | ResponseError<Error>> {
  const { t } = useTranslation();

  const saveNodesInCache = useSaveNodesInCache(surfaceId);
  const { findNodeById } = useFindNodeInCache(surfaceId);

  const [copySurfaceNodes] = useMutation(copySurfaceNodesMutation, {});

  return function (input: CopySurfaceNodesInput[]) {
    const isCopiedOnSameSurface = input[0].surface === surfaceId;

    return copySurfaceNodes({ variables: { input } }).then((response) => {
      if (response.isFailure) {
        return toast.error(response.getErrorMessage());
      }

      if (isCopiedOnSameSurface) {
        const copiedNodes = response.data;

        const containingNodes = copiedNodes
          .map((node) => node.contains)
          .filter(Boolean);

        saveNodesInCache((nodes) => [
          ...nodes,
          ...containingNodes,
          ...copiedNodes,
        ]);
      }

      toast.success(
        t(`Successfully copied nodes {{names}}`, {
          replace: {
            names:
              input
                .map(({ id }) => findNodeById(id))
                .map((node) => node?.name)
                .join(', ') || '',
          },
        }),
      );

      return response;
    });
  };
}
